import React, { useRef, useState } from 'react';
import { type Maybe, type SaleCalendar } from '@gql/schema';
import {
  type IRoomCapacity,
  RoomAndTravellerSection,
  RoomTravellerSelectionVariant,
  type TravellersSelection,
  createQueryParamFromSelection,
} from '@secretescapes/cauldron';
import {
  Button,
  ButtonSize,
  ButtonVariant,
  Panel,
  Popover,
  PopoverPosition,
  Scale,
  SelectOption,
  SelectVariant,
  css,
} from '@secretescapes/falcon';
import { i18n } from '@secretescapes/i18n';
import { useClickOutsideDetector } from '@utils/EventListeners.utils';
import { formatMomentToShortenedString } from '@utils/dates.utils';
import { setURLParams } from '@utils/url.utils';
import { useAppState } from '@utils/useAppState';
import { useApplicationState } from '@utils/useApplicationState';
import { useTranslations } from '@utils/useTranslations';
import moment from 'moment';
import { type IFlightState } from '@features/Flights';
import { FocusedInput, type TFocusedInput } from '../AvailabilityPresentation.interfaces';
import { ALL_DURATIONS, ALL_OFFERS, setCalendarURLParams } from '../AvailabilityPresentation.utils';
import { Calendar, CalendarSummary } from '../Calendar';
import { type CalendarDates, type NullableMoment } from '../Calendar/Calendar.interfaces';
import { LoadingSkeletonSection } from '../LoadingSkeleton/LoadingSkeleton';
import { Overlay } from '../Overlay';
import { useSelectionActions, useSelectionState } from '../SelectionContext';
import {
  Bar,
  CalendarActions,
  CalendarContainer,
  CalendarInputs,
  CalendarOverlayContainer,
  CalendarOverlayContent,
  DateInputsSkeleton,
  DropdownInputSkeleton,
  DropdownWrapper,
  Inputs,
  PanelWrapper,
  Selection,
  SelectionBarWrapper,
  StyledCustomSelect,
} from './AvailabilitySearchBar.styles';
import { CustomSelect } from './CustomSelect';
import { DateInputs } from './DateInputs';
import { DropdownInput, type Option } from './DropdownInput';
import { PanelInput } from './PanelInput/PanelInput';

interface Props {
  availability: Maybe<SaleCalendar>;
  checkInDate: NullableMoment;
  checkOutDate: NullableMoment;
  offerOptions: Option[];
  onDatesChange: (checkInDate: NullableMoment, checkOutDate: NullableMoment) => void;
  focusedInput: TFocusedInput;
  setFocusedInput: (input: FocusedInput | null) => void;
  onCalendarCancelled: () => void;
  availabilityLoading: boolean;
  withTabs: boolean;
  withFlights: boolean;
  isSameDates: boolean | undefined;
  flightState: IFlightState;
  onFlightStateChange: (arg: IFlightState) => void;
  airportOptions: Option[];
  durationOptions: Option[];
  roomCapacity: IRoomCapacity;
  scrollToCalendar: ((isStickyNavInactive?: boolean) => void) | undefined;
  animateOffersSelect: boolean;
  onAnimationChange: (value: boolean) => void;
  hasNoFlights: boolean;
}

export const AvailabilitySearchBar = ({
  availability,
  onDatesChange,
  checkInDate,
  checkOutDate,
  isSameDates,
  offerOptions,
  focusedInput,
  setFocusedInput,
  onCalendarCancelled,
  availabilityLoading,
  withTabs,
  withFlights,
  flightState,
  onFlightStateChange,
  airportOptions,
  durationOptions,
  roomCapacity,
  scrollToCalendar,
  animateOffersSelect,
  onAnimationChange,
  hasNoFlights,
}: Props) => {
  const isInputDisabled = hasNoFlights;

  const { setFinalRates } = useAppState();
  const { translations } = useTranslations();
  const { territoryLocale, isMobile } = useApplicationState();

  const { offerId, travellersSelection } = useSelectionState();
  const { changeOfferId, changeTravellersSelection } = useSelectionActions();

  const [isAirportPanelOpen, setAirportPanelOpen] = useState(false);

  const calendarRef = useRef<HTMLDivElement | null>(null);
  const dateInputRef = useRef<HTMLDivElement | null>(null);

  const airportLabel = i18n('common.departureAirport.label', [], translations);
  const durationLabel = i18n('common.duration.label', [], translations);
  const offersLabel = i18n('calendar.offer.dropdown.label', [], translations);
  const checkInLabel = i18n('common.checkIn.label', [], translations);
  const checkOutLabel = i18n('common.checkOut.label', [], translations);
  const dateInputPlaceholder = i18n('search.dates.placeholder', [], translations);
  const resetDatesLabel = i18n('search.dates.reset', [], translations);
  const confirmDatesLabel = i18n('common.date.confirm', [], translations);

  const checkInMobileFormat = formatMomentToShortenedString(checkInDate?.locale(territoryLocale));
  const checkOutMobileFormat = formatMomentToShortenedString(checkOutDate?.locale(territoryLocale));

  const closeCalendar = () => setFocusedInput(null);

  const closeCalendarOverlay = () => {
    closeCalendar();
    onCalendarCancelled();
  };

  useClickOutsideDetector(
    [calendarRef],
    () => {
      if (!isMobile) {
        closeCalendar();
        onCalendarCancelled();
      }
    },
    [dateInputRef]
  );

  const handleDatesChange = ({ startDate, endDate }: CalendarDates) => {
    onDatesChange(startDate ?? null, endDate ?? null);
  };

  const handleFocusChange = (input: TFocusedInput) => {
    if (isMobile) {
      setFocusedInput(input ?? FocusedInput.EndDate);
    } else if (input) {
      setFocusedInput(input);
    }
  };

  const handleDateInputAction = (input: TFocusedInput) => {
    setFocusedInput(input);

    if (!focusedInput) {
      if (!performance.getEntriesByName('start-render')?.length) {
        performance.mark('start-render');
      }

      if (!isMobile) {
        scrollToCalendar?.();
      }
    }
  };

  const handleResetClick = () => {
    changeOfferId(ALL_OFFERS);
    handleDatesChange({ startDate: undefined, endDate: undefined });
    handleFocusChange(FocusedInput.StartDate);
    setFinalRates(prev => ({
      ...prev,
      show: false,
      cheapestFinalRate: '',
      cheapestFinalRatePerRoomPerNight: '',
      numberOfTravellers: 2,
      numberOfNights: 1,
    }));
    setCalendarURLParams({ startDate: undefined, endDate: undefined });

    if (isMobile) {
      scrollToCalendar?.(false);
    }
  };

  const handleAirportSelect = (newValue: string) => {
    const newAirport = airportOptions.find(airport => airport.value === newValue);

    if (newAirport) {
      onFlightStateChange({
        ...flightState,
        selectedAirport: {
          code: newAirport.value,
          title: newAirport.text,
        },
      });
      setURLParams('airportCode', newValue);
      setURLParams('airportName', newAirport?.text);
    }

    if (isMobile) {
      setAirportPanelOpen(false);
    }
  };

  const handleDurationSelect = (newValue: string) => {
    const newDuration = durationOptions.find(night => night.value === newValue);

    if (newDuration) {
      onFlightStateChange({
        ...flightState,
        selectedNights: newDuration.value === ALL_DURATIONS ? null : Number(newDuration.value),
      });
      setURLParams('duration', newDuration.value === ALL_DURATIONS ? '' : newDuration.value);
      // Update checkOutDate to match new duration when dates selected.
      if (
        newDuration.value !== ALL_DURATIONS &&
        checkInDate &&
        checkOutDate &&
        moment(checkOutDate).startOf('d').diff(moment(checkInDate).startOf('d'), 'd') !==
          Number(newDuration.value)
      ) {
        const newCheckOutDate = moment(checkInDate).add(Number(newDuration.value), 'd');
        onDatesChange(checkInDate, newCheckOutDate);
        setCalendarURLParams({
          startDate: checkInDate,
          endDate: newCheckOutDate,
        });
      }
    }
  };

  const handleCtaClick = () => {
    closeCalendar();
    setCalendarURLParams({
      startDate: checkInDate,
      endDate: checkOutDate,
    });
    changeOfferId(ALL_OFFERS);

    if (isMobile) {
      scrollToCalendar?.(true);
    }
  };

  const handleTravellersSelectionConfirm = (selection: TravellersSelection) => {
    changeTravellersSelection(selection);
    setURLParams('travellersSelection', createQueryParamFromSelection(selection));
  };

  if (isMobile) {
    return (
      <>
        <SelectionBarWrapper>
          {/* Airport Input */}
          {withFlights && (
            <div>
              <PanelInput
                label={airportLabel}
                value={flightState.selectedAirport.title}
                onClick={() => setAirportPanelOpen(true)}
                isDisabled={isInputDisabled}
              />
              <PanelWrapper removeExtraPadding={true}>
                <Panel
                  title={airportLabel}
                  isOpen={isAirportPanelOpen}
                  onClose={() => setAirportPanelOpen(false)}
                  fullScreen={true}
                  dataAttributes={{ testid: 'selection-bar-panel' }}
                >
                  <DropdownWrapper>
                    {airportOptions.map(option => (
                      <SelectOption
                        key={option.text}
                        title={option.text}
                        subtitle={option.subtext ?? ''}
                        onClick={() => handleAirportSelect(option.value)}
                      />
                    ))}
                  </DropdownWrapper>
                </Panel>
              </PanelWrapper>
            </div>
          )}
          <DateInputs
            dateInputRef={dateInputRef}
            checkInLabel={checkInLabel}
            checkOutLabel={checkOutLabel}
            checkInValue={checkInMobileFormat}
            checkOutValue={checkOutMobileFormat}
            placeholderText={dateInputPlaceholder}
            focusedInput={focusedInput}
            onAction={handleDateInputAction}
            isDisabled={isInputDisabled}
          />
          {/* Travellers Input */}
          <RoomAndTravellerSection
            variant={RoomTravellerSelectionVariant.TOGGLE_OVERLAY}
            onConfirm={handleTravellersSelectionConfirm}
            selection={travellersSelection}
            maxCapacityInfo={roomCapacity}
            closeOnScroll={false}
          />
        </SelectionBarWrapper>
        <Overlay isOpen={!!focusedInput} onClose={closeCalendarOverlay}>
          <CalendarOverlayContent>
            <Selection>
              <StyledCustomSelect
                label={offersLabel}
                options={offerOptions}
                value={offerId}
                variant={SelectVariant.panel}
                onSelect={changeOfferId}
              />
              {withFlights && (
                <StyledCustomSelect
                  label={`${durationLabel}:`}
                  options={durationOptions}
                  value={
                    flightState.selectedNights
                      ? flightState.selectedNights.toString()
                      : ALL_DURATIONS
                  }
                  variant={SelectVariant.panel}
                  onSelect={handleDurationSelect}
                />
              )}
            </Selection>
            <CalendarOverlayContainer>
              <Calendar
                availability={availability}
                availabilityLoading={availabilityLoading}
                startDate={checkInDate}
                endDate={checkOutDate}
                focusedInput={focusedInput}
                onDatesChange={handleDatesChange}
                onFocusChange={handleFocusChange}
                withFlights={withFlights}
                rooms={travellersSelection.rooms.length}
              />
            </CalendarOverlayContainer>
          </CalendarOverlayContent>
          {checkInDate && checkOutDate && (
            <CalendarSummary
              isSameDates={!!isSameDates}
              onResetClick={handleResetClick}
              onConfirmClick={handleCtaClick}
            />
          )}
        </Overlay>
      </>
    );
  }

  return (
    <Bar withTabs={withTabs}>
      <Popover
        className="popover__parent"
        content={
          <CalendarContainer ref={calendarRef}>
            <CalendarInputs>
              <CustomSelect
                label={offersLabel}
                options={offerOptions}
                value={offerId}
                variant={SelectVariant.dropdown}
                animate={animateOffersSelect}
                fixedWidth={true}
                onSelect={changeOfferId}
                onAnimationEnd={() => onAnimationChange(false)}
              />

              {withFlights && (
                <CustomSelect
                  label={`${durationLabel}:`}
                  options={durationOptions}
                  value={
                    flightState.selectedNights
                      ? flightState.selectedNights.toString()
                      : ALL_DURATIONS
                  }
                  variant={SelectVariant.dropdown}
                  fixedWidth={true}
                  onSelect={handleDurationSelect}
                />
              )}
            </CalendarInputs>
            <Calendar
              onDatesChange={handleDatesChange}
              onFocusChange={handleFocusChange}
              startDate={checkInDate}
              endDate={checkOutDate}
              focusedInput={focusedInput}
              availability={availability}
              availabilityLoading={availabilityLoading}
              withFlights={withFlights}
              rooms={travellersSelection.rooms.length}
            />
            <div>
              <CalendarActions>
                <Button
                  dataAttributes={{ testid: 'reset-dates' }}
                  onClick={handleResetClick}
                  size={ButtonSize.medium}
                  variant={ButtonVariant.secondary}
                  disabled={!checkInDate && !checkOutDate}
                >
                  {resetDatesLabel}
                </Button>
                <Button
                  dataAttributes={{ testid: 'confirm-dates' }}
                  onClick={handleCtaClick}
                  disabled={!checkInDate || !checkOutDate || isSameDates}
                  size={ButtonSize.medium}
                >
                  {confirmDatesLabel}
                </Button>
              </CalendarActions>
            </div>
          </CalendarContainer>
        }
        noPadding={true}
        open={!!focusedInput}
        position={PopoverPosition.bottom}
        showPopoverArrow={false}
        triggerStyles={css`
          width: unset;
        `}
        withMaxWidth={false}
        zIndex={3}
      >
        <Inputs withFlights={withFlights}>
          {withFlights && (
            <DropdownInput
              label={airportLabel}
              options={airportOptions}
              value={flightState.selectedAirport.title}
              onSelect={handleAirportSelect}
              isDisabled={isInputDisabled}
            />
          )}
          <DateInputs
            dateInputRef={dateInputRef}
            checkInLabel={checkInLabel}
            checkOutLabel={checkOutLabel}
            checkInValue={checkInDate?.format('dddd LL') ?? ''}
            checkOutValue={checkOutDate?.format('dddd LL') ?? ''}
            placeholderText={dateInputPlaceholder}
            focusedInput={focusedInput}
            onAction={handleDateInputAction}
            isDisabled={isInputDisabled}
          />
          {/* Travellers Input */}
          <RoomAndTravellerSection
            variant={RoomTravellerSelectionVariant.TOGGLE_SELECT}
            onConfirm={handleTravellersSelectionConfirm}
            selection={travellersSelection}
            maxCapacityInfo={roomCapacity}
            closeOnScroll={false}
          />
        </Inputs>
      </Popover>
    </Bar>
  );
};

interface SkeletonProps {
  withTabs: boolean;
  flightsSelected: boolean;
  isMobile?: boolean;
}

const Skeleton = ({ withTabs, flightsSelected, isMobile }: SkeletonProps) => {
  return (
    <Bar data-testid="search-bar-skeleton" withTabs={withTabs} isMobile={isMobile}>
      {flightsSelected && (
        <DropdownInputSkeleton>
          <LoadingSkeletonSection height={isMobile ? '58px' : '72px'} borderRadius={Scale.xxs} />
        </DropdownInputSkeleton>
      )}
      <DateInputsSkeleton>
        <LoadingSkeletonSection height={isMobile ? '58px' : '72px'} borderRadius={Scale.xxs} />
      </DateInputsSkeleton>
      <DropdownInputSkeleton>
        <LoadingSkeletonSection height={isMobile ? '58px' : '72px'} borderRadius={Scale.xxs} />
      </DropdownInputSkeleton>
    </Bar>
  );
};

AvailabilitySearchBar.Skeleton = Skeleton;
